import { useState, useEffect } from "react";
import { Wheel } from "react-custom-roulette";
import Confetti from "react-confetti";
import clienteAxios from "../../axios";
import Cargador from "../../componentes/Cargador";

/**
 * Modal de la ruleta de premios.
 *
 * ---------------------------------------------------------------------------
 * SOBRE EL TAMAÑO
 * ---------------------------------------------------------------------------
 * `react-custom-roulette` dimensiona su contenedor contra el VIEWPORT, no
 * contra el elemento que lo contiene:
 *
 *     width: 80vw; max-width: 445px;   (y lo mismo en alto)
 *
 * La tarjeta usaba `md:w-1/4`, que es otro porcentaje del viewport, así que
 * ambos crecían a ritmos distintos: en tablet (768px) la tarjeta medía 192px y
 * la rueda 445px, y se salía por los lados.
 *
 * Por eso el ancho ahora es fijo (`max-w-[520px]`) en vez de proporcional:
 * 520 − 48 de padding = 472px de contenido, suficiente para los 445px que la
 * rueda alcanza como máximo. Y hacia abajo, con `p-3` en el fondo y `p-4` en
 * la tarjeta, quedan `100vw − 56px` de contenido contra los `80vw` de la
 * rueda: entra en cualquier pantalla de 280px en adelante.
 */
const RuletaPremios = ({ onClose, correo, ListaPremios, idRespuesta, premioSeleccionado }) => {
  const [mustSpin, setMustSpin] = useState(false);
  const [prizeNumber, setPrizeNumber] = useState(0);
  const [wheelSpun, setWheelSpun] = useState(false); // Nuevo estado para controlar si ya se ha girado la rueda
  const [showConfetti, setShowConfetti] = useState(false);
  const [premio, setPremio] = useState(null);
  const [imagen, setImagen] = useState(null);

  /**
   * El confeti se dibuja sobre un lienzo de tamaño fijo. Antes se leía
   * `window.innerWidth/Height` durante el render, así que al girar el teléfono
   * el lienzo conservaba las medidas de la orientación anterior y el confeti
   * quedaba recortado a media pantalla.
   */
  const [ventana, setVentana] = useState({
    ancho: window.innerWidth,
    alto: window.innerHeight,
  });

  useEffect(() => {
    const alRedimensionar = () =>
      setVentana({ ancho: window.innerWidth, alto: window.innerHeight });

    window.addEventListener("resize", alRedimensionar);
    window.addEventListener("orientationchange", alRedimensionar);

    return () => {
      window.removeEventListener("resize", alRedimensionar);
      window.removeEventListener("orientationchange", alRedimensionar);
    };
  }, []);

  const girar = () => {
    setMustSpin(true);
    setWheelSpun(true);
    setPrizeNumber(premioSeleccionado.indexs);
    setPremio(premioSeleccionado.descripcion);
    setImagen(premioSeleccionado.url);
  };

  const handleStop = () => {
    setMustSpin(false);
    setShowConfetti(true);
    enviarPremioCorreo();
  };

  const enviarPremioCorreo = () => {
    const data = {
      "sp": "respuestas",
      "op": "actualizar",
      "correo": correo ? correo : "alberto@cruzespinosa.com",
      "premio": premio,
      "Id": idRespuesta,
      "idPremio": premioSeleccionado.idPremio
    }

    clienteAxios.post("/api/sp", data)
      .then(response => {
        console.log("Correo enviado:", response.data);
      })
      .catch(error => {
        console.error("Error al enviar correo:", error);
      });
  }

  // Menos piezas en pantallas chicas: el confeti se recicla de forma continua
  // y en un teléfono 300 piezas animándose sin parar calientan el equipo.
  const piezasConfeti = ventana.ancho < 640 ? 150 : 300;

  return (
    /**
     * El fondo es el que desplaza, no la tarjeta. En un teléfono en horizontal
     * la ruleta más el texto del premio no caben en la altura visible, y con
     * `items-center` sobre un contenedor sin scroll el contenido se recortaba
     * arriba y abajo sin forma de alcanzarlo.
     */
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-black/70 p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="titulo-ruleta"
    >
      {showConfetti && (
        <Confetti
          width={ventana.ancho}
          height={ventana.alto}
          recycle={true}
          numberOfPieces={piezasConfeti}
        />
      )}

      <div className="flex min-h-full items-center justify-center">
        <div className="w-full max-w-[520px] rounded-3xl bg-white p-4 text-center shadow-lg sm:p-6">
          {!ListaPremios ? (
            <Cargador texto="Preparando la ruleta…" />
          ) : (
            <>
              <h2 id="titulo-ruleta" className="mb-4 text-xl font-semibold sm:text-2xl">
                ¡Ruleta de Prueba!
              </h2>

              {!showConfetti && (
                // La rueda se centra desde aquí: su contenedor es de ancho
                // fijo en vw y no se alinea solo dentro de la tarjeta.
                <div className="flex justify-center">
                  <Wheel
                    mustStartSpinning={mustSpin}
                    prizeNumber={prizeNumber}
                    data={ListaPremios ? ListaPremios : []}
                    backgroundColors={["#3e3e3e", "#3e3e3e"]}
                    textColors={["#ffffff"]}
                    onStopSpinning={() => {
                      handleStop();
                    }}
                  />
                </div>
              )}
            </>
          )}

          {!wheelSpun && (
            <button
              onClick={girar}
              // Ancho completo en móvil y `py-3`: un botón de 32px de alto es
              // difícil de acertar con el pulgar.
              className="mt-4 w-full rounded-lg bg-orange-500 px-6 py-3 font-medium text-white hover:bg-orange-600 sm:w-auto"
            >
              ¡Gira y mucha suerte!
            </button>
          )}

          {wheelSpun && !mustSpin && (
            <>
              <p className="px-1 py-4 text-base font-semibold sm:text-lg">
                Recibe tu premio al correo{" "}
                <span className="break-all text-blue-600">{correo}</span>, para ser válido para el
                premio {premio}
              </p>

              <p className="text-sm font-bold sm:text-base">
                ¡Aprovecha tu cupón! Canjea tu beneficio de martes a viernes con vigencia de 1 mes.
              </p>

              {imagen && (
                <img
                  src={imagen}
                  // `max-h` + `w-auto`: con el ancho al 50% de la tarjeta, un
                  // premio con imagen apaisada salía deformado.
                  className="mx-auto mt-4 max-h-36 w-auto object-contain sm:max-h-44"
                  alt={premio || "Premio"}
                />
              )}

              <button
                onClick={() => { onClose(); }}
                className="mt-4 w-full rounded-lg bg-blue-500 px-6 py-3 font-medium text-white hover:bg-blue-600 sm:w-auto"
              >
                Cerrar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RuletaPremios;
